"use client";

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useApi } from "@/lib/hooks/use-api";
import { queryKeys, type QueryFilters } from "@/lib/query-keys";

export const useDriversList = (filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.drivers.list(filters),
    queryFn: () => api.drivers.list(filters as any),
    placeholderData: keepPreviousData,
  });
};

export const useDriverDetail = (id?: string, enabled = true) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.drivers.detail(id || ""),
    enabled: Boolean(id) && enabled,
    queryFn: async () => {
      if (!id) return undefined;
      return api.drivers.get(id);
    },
  });
};

export const useCreateDriver = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.drivers.create>[0]) =>
      api.drivers.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.lists() });
    },
  });
};

export const useUpdateDriver = (id: string) => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.drivers.update>[1]) =>
      api.drivers.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.lists() });
    },
  });
};

export const useDeleteDriver = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (id: string) => api.drivers.delete(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.lists() });
    },
  });
};

export const useDriverActiveShipment = (id?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: [...queryKeys.drivers.detail(id || ""), "active-shipment"],
    enabled: Boolean(id),
    queryFn: async () => {
      if (!id) return null;
      return api.drivers.getActiveShipment(id);
    },
    staleTime: 30 * 1000,
  });
};
